import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ClockCounterClockwise, Sparkle } from '@phosphor-icons/react';

import {
  BottomSheet,
  BottomSheetContent,
} from '../common/BottomSheet';
import pt from '../../locales/pt';
import { AiMessageContent } from './AiMessageContent';
import { EvolutionAiComposer } from './EvolutionAiComposer';
import {
  useAiSessionQuery,
  useCurrentPageId,
  useEvolutionAiChatMutation,
  usePageAiContextQuery,
} from '../../features/ai/queries';
import { useEvolutionAi } from '../../store/EvolutionAiContext';
import { cn } from '../../utils/cn';
import { EvolutionAiHistoryPanel } from './EvolutionAiHistoryPanel';

function ChatBubble({ role, content }) {
  const isUser = role === 'user';
  return (
    <div
      className={cn(
        'max-w-[92%] rounded-2xl px-3 py-2 text-sm leading-relaxed',
        isUser
          ? 'ml-auto bg-emerald-700 text-white'
          : 'mr-auto bg-muted text-foreground',
      )}
    >
      {isUser ? content : <AiMessageContent content={content} />}
    </div>
  );
}

function DrawerEmptyState({ questions, onSelect }) {
  return (
    <div className="flex flex-col items-center px-1 pb-2 pt-2 text-center">
      <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-sage-600 to-emerald-700 text-white shadow-sm">
        <Sparkle className="h-6 w-6" aria-hidden />
      </div>
      <h3 className="font-display text-lg font-semibold text-foreground">
        {pt.evolutionAi.title}
      </h3>
      <p className="mt-1 max-w-xs text-sm text-muted-foreground">{pt.evolutionAi.subtitle}</p>

      {questions.length ? (
        <div className="mt-6 w-full text-left">
          <p className="mb-3 text-sm font-medium text-muted-foreground">
            {pt.evolutionAi.getStarted}
          </p>
          <ul className="flex flex-col gap-2">
            {questions.map((question) => (
              <li key={question}>
                <button
                  type="button"
                  onClick={() => onSelect(question)}
                  className={cn(
                    'flex w-full items-start gap-3 rounded-xl border border-border bg-background px-3 py-3',
                    'text-left text-sm leading-snug text-muted-foreground',
                    'transition hover:border-sage-200 hover:bg-sage-50 dark:hover:bg-sage-900/80',
                  )}
                >
                  <Sparkle className="mt-0.5 h-4 w-4 shrink-0 text-sage-600" aria-hidden />
                  <span>{question}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="mt-6 text-sm text-muted-foreground">{pt.evolutionAi.emptyHint}</p>
      )}
    </div>
  );
}

function HistoryHeaderButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex h-10 w-10 items-center justify-center rounded-full',
        'border border-border bg-card text-muted-foreground',
        'transition hover:bg-muted hover:text-foreground active:scale-95',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 focus-visible:ring-offset-2',
      )}
      aria-label={pt.evolutionAi.history}
    >
      <ClockCounterClockwise className="h-[1.125rem] w-[1.125rem]" weight="regular" aria-hidden />
    </button>
  );
}

/**
 * Drawer de chat do Evolution AI (BottomSheet mobile / diálogo desktop).
 */
export function EvolutionAiDrawer() {
  const pageId = useCurrentPageId();
  const { isOpen, setOpen, seedMessage, setSeedMessage, closeAssistant } = useEvolutionAi();
  const { data: pageContext } = usePageAiContextQuery(pageId, { enabled: isOpen });
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [viewMode, setViewMode] = useState('chat');
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [sessionPageId, setSessionPageId] = useState(null);
  const [loadingSessionId, setLoadingSessionId] = useState(null);
  const chatMutation = useEvolutionAiChatMutation();
  const messagesEndRef = useRef(null);

  const scrollToLatest = useCallback(() => {
    const node = messagesEndRef.current;
    if (typeof node?.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
  }, []);

  const { data: loadedSession, isFetching: isLoadingSession } = useAiSessionQuery(loadingSessionId, {
    enabled: Boolean(loadingSessionId),
  });

  const suggestedQuestions = pageContext?.suggested_questions || [];
  const effectivePageId = sessionPageId || pageId;
  const hasConversation = messages.length > 0;
  const isHistoryView = viewMode === 'history';

  const handleSend = useCallback(
    async (text) => {
      const trimmed = (text || '').trim();
      if (!trimmed || chatMutation.isPending) return;

      let historySnapshot = [];
      setMessages((prev) => {
        historySnapshot = prev;
        return [...prev, { role: 'user', content: trimmed }];
      });
      setInput('');

      try {
        const result = await chatMutation.mutateAsync({
          pageId: effectivePageId,
          message: trimmed,
          sessionId: activeSessionId,
          history: activeSessionId ? [] : historySnapshot,
        });
        if (result?.session_id) {
          setActiveSessionId(result.session_id);
          if (!sessionPageId) {
            setSessionPageId(effectivePageId);
          }
        }
        const answer =
          result?.answer ||
          (result?.blocked ? pt.evolutionAi.offTopic : pt.evolutionAi.error);
        setMessages((prev) => [...prev, { role: 'assistant', content: answer }]);
      } catch (error) {
        const answer = error?.rateLimited
          ? pt.evolutionAi.rateLimited
          : error?.message || pt.evolutionAi.error;
        setMessages((prev) => [...prev, { role: 'assistant', content: answer }]);
      }
    },
    [activeSessionId, chatMutation, effectivePageId, sessionPageId],
  );

  useEffect(() => {
    if (!loadedSession || !loadingSessionId) return;

    setActiveSessionId(loadedSession.id);
    setSessionPageId(loadedSession.page_id);
    setMessages(
      (loadedSession.messages || []).map((message) => ({
        role: message.role,
        content: message.content,
      })),
    );
    setLoadingSessionId(null);
    setViewMode('chat');
  }, [loadedSession, loadingSessionId]);

  useEffect(() => {
    if (!isOpen || !seedMessage.trim() || isHistoryView) return;

    const message = seedMessage.trim();
    setSeedMessage('');
    void handleSend(message);
  }, [isOpen, isHistoryView, seedMessage, setSeedMessage, handleSend]);

  useEffect(() => {
    if (!isOpen) {
      setViewMode('chat');
      setActiveSessionId(null);
      setSessionPageId(null);
      setLoadingSessionId(null);
      setMessages([]);
      setInput('');
    }
  }, [isOpen]);

  useLayoutEffect(() => {
    if (isHistoryView) return;
    scrollToLatest();
    const frame = requestAnimationFrame(scrollToLatest);
    return () => cancelAnimationFrame(frame);
  }, [isHistoryView, messages, chatMutation.isPending, scrollToLatest]);

  const handleSelectSession = (sessionId) => {
    setLoadingSessionId(sessionId);
  };

  const handleOpenHistory = () => {
    setViewMode('history');
  };

  const handleBackToChat = () => {
    setViewMode('chat');
  };

  return (
    <BottomSheet open={isOpen} onOpenChange={(open) => (open ? setOpen(true) : closeAssistant())}>
      <BottomSheetContent
        fullscreenMobile
        title={!isHistoryView && hasConversation ? pt.evolutionAi.title : undefined}
        description={!isHistoryView && hasConversation ? pt.evolutionAi.subtitle : undefined}
        headerStart={
          !isHistoryView ? (
            <HistoryHeaderButton onClick={handleOpenHistory} />
          ) : null
        }
        className="flex flex-col"
        footer={
          isHistoryView ? null : (
            <EvolutionAiComposer
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onSubmit={() => handleSend(input)}
              disabled={isLoadingSession}
              isSending={chatMutation.isPending}
            />
          )
        }
      >
        {isHistoryView ? (
          <EvolutionAiHistoryPanel
            onSelectSession={handleSelectSession}
            onBack={handleBackToChat}
          />
        ) : (
          <div
            className={cn(
              'flex min-h-0 flex-1 flex-col gap-3 pb-2 md:min-h-[14rem]',
              !hasConversation && 'pt-[max(1rem,env(safe-area-inset-top))]',
            )}
          >
            {isLoadingSession ? (
              <p className="text-sm text-muted-foreground">{pt.common.loading}</p>
            ) : null}
            {!hasConversation && !isLoadingSession ? (
              <DrawerEmptyState
                questions={suggestedQuestions}
                onSelect={(question) => handleSend(question)}
              />
            ) : null}
            {messages.map((msg, index) => (
              <ChatBubble key={`${msg.role}-${index}`} role={msg.role} content={msg.content} />
            ))}
            {chatMutation.isPending && hasConversation ? (
              <p className="text-xs text-muted-foreground/70">{pt.evolutionAi.thinking}</p>
            ) : null}
            <div ref={messagesEndRef} aria-hidden className="h-px shrink-0" />
          </div>
        )}
      </BottomSheetContent>
    </BottomSheet>
  );
}
